import React, { useState, useMemo } from 'react';
import { Popover, Box, Typography, InputBase } from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PushPinRoundedIcon from '@mui/icons-material/PushPinRounded';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useQueryClient } from '@tanstack/react-query';
import { Folder, pinFolder, unpinFolder, reorderPins, FOLDER_PIN_LIMIT } from '../../services/folders_api';
import { folderCache } from '../../services/folders_cache';
import { FolderGlyph, folderColor } from './folderVisuals';

interface FolderListPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  folders: Folder[];
  selectedFolderId: number | null;
  onSelect: (folderId: number | null) => void;
  onCreate: () => void;
  onManage: () => void;
  onEnterSelection: () => void;
}

// 1行ぶんの中身（グリフ＋名前＋件数＋ピントグル）。
const FolderRowContent: React.FC<{
  folder: Folder;
  selected: boolean;
  onSelect: () => void;
  onTogglePin: () => void;
}> = ({ folder, selected, onSelect, onTogglePin }) => {
  const color = folderColor(folder.color);
  const isPinned = folder.pin_order != null;
  return (
    <>
      <Box
        onClick={onSelect}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flex: 1, minWidth: 0, cursor: 'pointer', py: 0.1 }}
      >
        <FolderGlyph iconKey={folder.icon} open={selected} sx={{ fontSize: '1rem', color, flexShrink: 0 }} />
        <Typography
          sx={{
            flex: 1, minWidth: 0, fontSize: '0.8rem',
            fontWeight: selected ? 600 : 400,
            color: selected ? 'text.primary' : 'rgba(255,255,255,0.78)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
        >
          {folder.name}
        </Typography>
        <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: 'rgba(255,255,255,0.3)' }}>
          {folder.item_count}
        </Typography>
      </Box>
      <Box
        onClick={(e) => { e.stopPropagation(); onTogglePin(); }}
        title={isPinned ? 'ピンを外す' : 'ピン留め'}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 26, height: 26, borderRadius: '6px', cursor: 'pointer', flexShrink: 0,
          color: isPinned ? 'primary.main' : 'rgba(255,255,255,0.3)',
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.07)',
            color: isPinned ? 'primary.main' : 'rgba(255,255,255,0.65)',
          },
        }}
      >
        {isPinned
          ? <PushPinRoundedIcon sx={{ fontSize: '0.95rem' }} />
          : <PushPinOutlinedIcon sx={{ fontSize: '0.95rem' }} />}
      </Box>
    </>
  );
};

const rowSx = {
  display: 'flex', alignItems: 'center', gap: 0.3,
  px: 0.5, borderRadius: '8px',
  '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
} as const;

// ピン留めセクションの行（ドラッグで並べ替え可能）。
const SortablePinRow: React.FC<{
  folder: Folder;
  selected: boolean;
  onSelect: () => void;
  onTogglePin: () => void;
}> = ({ folder, selected, onSelect, onTogglePin }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: folder.id });
  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{ ...rowSx, backgroundColor: isDragging ? 'rgba(255,255,255,0.07)' : 'transparent' }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          display: 'flex', alignItems: 'center', cursor: 'grab',
          color: 'rgba(255,255,255,0.25)', touchAction: 'none',
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <DragIndicatorRoundedIcon sx={{ fontSize: '1rem' }} />
      </Box>
      <FolderRowContent folder={folder} selected={selected} onSelect={onSelect} onTogglePin={onTogglePin} />
    </Box>
  );
};

// 非ピン／検索結果の行（ドラッグハンドルなし。位置合わせ用にスペーサー）。
const PlainRow: React.FC<{
  folder: Folder;
  selected: boolean;
  onSelect: () => void;
  onTogglePin: () => void;
}> = ({ folder, selected, onSelect, onTogglePin }) => (
  <Box sx={rowSx}>
    <Box sx={{ width: 20, flexShrink: 0 }} />
    <FolderRowContent folder={folder} selected={selected} onSelect={onSelect} onTogglePin={onTogglePin} />
  </Box>
);

const SectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{ px: 1, pt: 1, pb: 0.4, fontSize: '0.66rem', fontWeight: 700, color: 'rgba(255,255,255,0.4)' }}
  >
    {children}
  </Typography>
);

const FooterBtn: React.FC<{ icon: React.ReactNode; label: string; onClick: () => void }> = ({ icon, label, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2,
      py: 0.85, cursor: 'pointer', color: 'rgba(255,255,255,0.6)',
      '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)', color: 'primary.main' },
      '& svg': { fontSize: '1.05rem' },
    }}
  >
    {icon}
    <Typography sx={{ fontSize: '0.66rem', fontWeight: 600 }}>{label}</Typography>
  </Box>
);

// フィルター行のフォルダアイコンから開くハブ。検索・選択・ピン操作・新規/管理の入口。
const FolderListPopover: React.FC<FolderListPopoverProps> = ({
  open, anchorEl, onClose, folders, selectedFolderId, onSelect, onCreate, onManage, onEnterSelection,
}) => {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [pinError, setPinError] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const q = query.trim().toLowerCase();
  const filtered = useMemo(
    () => (q ? folders.filter((f) => f.name.toLowerCase().includes(q)) : folders),
    [folders, q],
  );
  const pinned = useMemo(
    () => filtered.filter((f) => f.pin_order != null)
      .sort((a, b) => (a.pin_order as number) - (b.pin_order as number)),
    [filtered],
  );
  const others = useMemo(
    () => filtered.filter((f) => f.pin_order == null).sort((a, b) => a.order_num - b.order_num),
    [filtered],
  );
  const pinnedTotal = folders.filter((f) => f.pin_order != null).length;

  const close = () => {
    setQuery('');
    setPinError(null);
    onClose();
  };
  const handleSelect = (id: number) => { onSelect(id); close(); };
  const footerAction = (cb: () => void) => () => { close(); cb(); };

  const handleTogglePin = async (folder: Folder) => {
    setPinError(null);
    try {
      const updated = folder.pin_order != null
        ? await unpinFolder(folder.id)
        : await pinFolder(folder.id);
      folderCache.update(queryClient, updated);
    } catch (e: any) {
      const detail = e?.response?.data;
      setPinError(
        (Array.isArray(detail) && detail[0]) ||
        detail?.detail ||
        (typeof detail === 'string' ? detail : null) ||
        `ピン留めは最大${FOLDER_PIN_LIMIT}個までです`,
      );
    }
  };

  const handleDragEnd = async (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = pinned.findIndex((f) => f.id === active.id);
    const newIndex = pinned.findIndex((f) => f.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const ids = arrayMove(pinned, oldIndex, newIndex).map((f) => f.id);
    folderCache.applyPinOrder(queryClient, ids); // 楽観更新
    try {
      await reorderPins(ids);
    } catch (e) {
      console.error('ピンの並べ替えに失敗しました', e);
      folderCache.invalidate(queryClient);
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={close}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{
        sx: {
          width: 300, mt: 0.5, borderRadius: '12px',
          border: '1px solid', borderColor: 'divider',
          boxShadow: '0 10px 32px rgba(0,0,0,0.5)',
        },
      }}
    >
      {/* 検索 */}
      <Box sx={{ p: 1 }}>
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5,
            borderRadius: '8px', backgroundColor: 'rgba(255,255,255,0.05)',
          }}
        >
          <SearchRoundedIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.4)' }} />
          <InputBase
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="フォルダを検索"
            sx={{ flex: 1, fontSize: '0.8rem', color: 'text.primary' }}
          />
        </Box>
      </Box>

      {pinError && (
        <Typography sx={{ px: 1.5, pb: 0.5, fontSize: '0.68rem', color: 'error.main' }}>
          {pinError}
        </Typography>
      )}

      {/* 一覧 */}
      <Box sx={{ maxHeight: 320, overflowY: 'auto', px: 0.5, pb: 0.5 }}>
        {folders.length === 0 ? (
          <Typography sx={{ px: 1, py: 2.5, fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
            フォルダがありません
          </Typography>
        ) : q ? (
          // 検索中はフラット表示（ドラッグ並べ替えなし）
          filtered.length === 0 ? (
            <Typography sx={{ px: 1, py: 2.5, fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
              一致するフォルダがありません
            </Typography>
          ) : (
            [...pinned, ...others].map((f) => (
              <PlainRow
                key={f.id}
                folder={f}
                selected={selectedFolderId === f.id}
                onSelect={() => handleSelect(f.id)}
                onTogglePin={() => handleTogglePin(f)}
              />
            ))
          )
        ) : (
          <>
            {pinned.length > 0 && (
              <>
                <SectionHeader>ピン留め {pinnedTotal}/{FOLDER_PIN_LIMIT}</SectionHeader>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={pinned.map((f) => f.id)} strategy={verticalListSortingStrategy}>
                    {pinned.map((f) => (
                      <SortablePinRow
                        key={f.id}
                        folder={f}
                        selected={selectedFolderId === f.id}
                        onSelect={() => handleSelect(f.id)}
                        onTogglePin={() => handleTogglePin(f)}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </>
            )}
            {others.length > 0 && (
              <>
                <SectionHeader>その他</SectionHeader>
                {others.map((f) => (
                  <PlainRow
                    key={f.id}
                    folder={f}
                    selected={selectedFolderId === f.id}
                    onSelect={() => handleSelect(f.id)}
                    onTogglePin={() => handleTogglePin(f)}
                  />
                ))}
              </>
            )}
          </>
        )}
      </Box>

      {/* フッター操作 */}
      <Box sx={{ display: 'flex', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <FooterBtn icon={<AddRoundedIcon />} label="新規" onClick={footerAction(onCreate)} />
        <FooterBtn icon={<ChecklistRoundedIcon />} label="まとめて選択" onClick={footerAction(onEnterSelection)} />
        <FooterBtn icon={<TuneRoundedIcon />} label="管理" onClick={footerAction(onManage)} />
      </Box>
    </Popover>
  );
};

export default FolderListPopover;
